import logoUrl from "@/assets/logo/Becoming-Alpha-Logos-Fibonacci.svg";
import logoTextUrl from "@/assets/logo/Becoming-Alpha-Logos-Fibonacci-Text.svg";
import logoTextSvgContent from "@/assets/logo/Becoming-Alpha-Logos-Fibonacci-Text.svg?raw";
import DOMPurify from "dompurify";

interface LogoProps {
  className?: string;
  gradientId?: string;
  variant?: "default" | "text";
  maxWidth?: string | number;
  gradient?: boolean;
  width?: number;
  height?: number;
  loading?: "lazy" | "eager";
}

/**
 * Logo - Brand logo component with Fibonacci spiral design
 *
 * Features:
 * - SVG-based logo with "BECOMING ALPHA" text and Fibonacci spiral
 * - Supports two variants: "default" (full logo) and "text" (text-only)
 * - Configurable gradient ID for multiple instances (for future use)
 * - Accessible with aria-label
 * - Responsive sizing via className
 * - Optional max-width constraint
 * - Optional gradient effect (same as active menu items)
 *
 * @example
 * <Logo className="h-12 w-auto" gradientId="paint0_radial_header" />
 * <Logo variant="text" className="h-8 w-auto" maxWidth="200px" />
 * <Logo variant="text" maxWidth={150} gradient />
 */
export default function Logo({
  className = "h-12 w-auto",
  gradientId: _gradientId = "paint0_radial_logo",
  variant = "default",
  maxWidth,
  gradient = false,
  width,
  height,
  loading = "lazy",
}: LogoProps) {
  const logoSrc = variant === "text" ? logoTextUrl : logoUrl;

  // Calculate dimensions from className if not provided
  // Extract height from className (e.g., "h-36" = 144px, "h-12" = 48px)
  let calculatedHeight = height;
  let calculatedWidth = width;

  if (!calculatedHeight || !calculatedWidth) {
    const heightMatch = className.match(/h-(\d+)/);
    if (heightMatch && !calculatedHeight) {
      const heightValue = parseInt(heightMatch[1], 10);
      calculatedHeight = heightValue * 4; // Tailwind: h-1 = 4px, h-12 = 48px, h-36 = 144px
    }

    // Default aspect ratios: default logo ~1.5:1, text logo ~4:1
    if (!calculatedWidth) {
      if (variant === "text") {
        calculatedWidth = calculatedHeight ? calculatedHeight * 4 : 320; // Text logo is wider
      } else {
        calculatedWidth = calculatedHeight ? calculatedHeight * 1.5 : 192; // Default logo
      }
    }

    if (!calculatedHeight) {
      calculatedHeight = calculatedWidth
        ? calculatedWidth / (variant === "text" ? 4 : 1.5)
        : 144;
    }
  }

  // Ensure we always have valid dimensions (fallback values)
  calculatedWidth = calculatedWidth || (variant === "text" ? 320 : 192);
  calculatedHeight = calculatedHeight || (variant === "text" ? 80 : 128);

  const maxWidthStyle = maxWidth
    ? { maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth }
    : undefined;

  if (gradient && variant === "text") {
    // For gradient effect, render SVG inline and apply gradient class
    // Replace the class="b" with class="b gradient" in the SVG content
    let svgWithGradient = logoTextSvgContent.replace(/class="b"/g, 'class="b gradient"');

    // Show the underline by changing its opacity
    svgWithGradient = svgWithGradient.replace(
      /class="underline"/,
      'class="underline" style="opacity: 1;"'
    );

    // Add className and style to the root SVG element
    const styleAttr = maxWidthStyle
      ? `style="max-width: ${typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth};"`
      : "";
    svgWithGradient = svgWithGradient.replace(
      /<svg([^>]*)>/,
      `<svg$1 class="${DOMPurify.sanitize(className)}" ${styleAttr} aria-label="Becoming Alpha logo" role="img">`
    );

    // Add accessible title and description to SVG for screen readers
    if (!svgWithGradient.includes("<title>")) {
      svgWithGradient = svgWithGradient.replace(
        /(<svg[^>]*>)/,
        "$1<title>Becoming Alpha</title><desc>Becoming Alpha logo with text</desc>"
      );
    }

    // Sanitize SVG content to prevent XSS attacks
    const sanitizedSvg = DOMPurify.sanitize(svgWithGradient, {
      USE_PROFILES: { svg: true, svgFilters: true },
      ADD_TAGS: ["svg", "path", "g", "defs", "linearGradient", "stop", "title", "desc"],
      ADD_ATTR: [
        "class",
        "style",
        "viewBox",
        "xmlns",
        "fill",
        "d",
        "id",
        "gradientUnits",
        "x1",
        "y1",
        "x2",
        "y2",
        "offset",
        "stop-color",
        "aria-label",
      ],
    });

    return (
      <span className="inline-block" dangerouslySetInnerHTML={{ __html: sanitizedSvg }} />
    );
  }

  return (
    <img
      src={logoSrc}
      alt="Becoming Alpha logo"
      className={className}
      style={maxWidthStyle}
      width={calculatedWidth}
      height={calculatedHeight}
      aria-label="Becoming Alpha logo"
      loading={loading}
    />
  );
}
